import { makeStyles } from '@material-ui/styles'
import { useAppSelector } from 'app/store'
import { ArrayElement } from 'core/actions/Action'
import GridSearchFilter from 'core/elements/grid/GridSearchFilter'
import Text from 'core/elements/Text'
import useListAction from 'core/hooks/useListAction'
import { listByoHosts } from 'k8s/components/nodes/byoh/actions'
import { physicalNodeSelector, UNASSIGNED_CLUSTER_NAME } from 'k8s/components/nodes/byoh/selectors'
import Grid, { GridViewColumn } from 'pf9-ui-components/built/elements/grid/Grid'
import Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import { path } from 'ramda'
import React, { useEffect } from 'react'

const useVmExpandedRowStyles = makeStyles((theme: Theme) => ({
  expandedRowContainer: {
    display: 'grid',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    marginLeft: '42px',
  },
  vmFlavorsTable: {
    '& > div > div > div > div': {
      '&:first-child > div:last-child > div:last-child': {
        display: 'none',
      },
      '&:last-child': {
        gap: 0,
      },
    },
  },
}))

type SelectorModel = ArrayElement<ReturnType<typeof physicalNodeSelector>>

const columns: GridViewColumn<SelectorModel>[] = [
  { key: 'name', label: 'Name' },
  {
    key: 'ipAddresses',
    label: 'IP Addresses',
    CellComponent: ({ value }) => {
      return (
        <>
          {value.map((iface) => (
            <Text key={iface} variant="body2">
              {iface}
            </Text>
          ))}
        </>
      )
    },
  },
  {
    key: 'status.hostinfo.osimage',
    label: 'OS Image',
  },
  {
    key: 'status.hostinfo.osname',
    label: 'OS Name',
  },
]

const searchTargets = ['name']
const globalFilters = [
  {
    key: 'search',
    equalityComparerFn: (item, value) => {
      return !!searchTargets.find((key) =>
        String(
          Object.prototype.hasOwnProperty.call(item, key)
            ? item[String(key)]
            : path(String(key).split('.'), item),
        )
          .toLocaleLowerCase()
          .includes(value.toLocaleLowerCase()),
      )
    },
    FilterComponent: GridSearchFilter,
  },
]

/**
 * @summary PhysicalNodesTable component
 * @param selectedNodes - Selected nodes
 * @param getParamsUpdater - Function to update params
 * @returns PhysicalNodesTable component is used to display the list of physical nodes for cluster creation or adding nodes to existing cluster
 */
export default function PhysicalNodesTable({
  selectedNodes,
  getParamsUpdater,
  setError = undefined,
}) {
  const classes = useVmExpandedRowStyles()

  const { loading: loadingPhysicalNodes, reload } = useListAction(listByoHosts)
  const data = useAppSelector(physicalNodeSelector)
  const filteredData = data?.filter((node) => node?.clusterName === UNASSIGNED_CLUSTER_NAME)

  // Set initial load
  useEffect(() => {
    reload(true)
  }, [])

  useEffect(() => {
    if (loadingPhysicalNodes) return

    if (filteredData?.length === 0 && setError) {
      setError({
        title: 'No Physical Nodes available',
      })
    }
  }, [filteredData, loadingPhysicalNodes])

  return (
    <div className={classes.vmFlavorsTable} data-testid="physical-nodes-table">
      <Grid<SelectorModel>
        uniqueIdentifier="uid"
        columns={columns}
        data={filteredData}
        label="Physical Nodes"
        orderBy="name"
        orderDirection="asc"
        emptyContent="No Physical Nodes available"
        disableRowSelection={false}
        multiSelection
        selectedItems={selectedNodes}
        onSelectChange={(selectedItems) => getParamsUpdater('physicalNodes')(selectedItems)}
        disableColumnHiding
        loading={loadingPhysicalNodes}
        loadingMessage="Loading Physical Nodes..."
        globalFilters={globalFilters}
      />
    </div>
  )
}
